import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../utils/supabaseClient";
import {
  getSupportMessages,
  sendSupportMessage,
  markSupportRead,
  getSupportConversations,
} from "../Services/supportService";

/**
 * Agent-side live chat for one conversation.
 * @param {string|null} conversationId
 */
export const useSupportChat = (conversationId) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const seenIds = useRef(new Set());

  const upsertMessage = useCallback((msg) => {
    if (!msg || seenIds.current.has(msg.id)) return;
    seenIds.current.add(msg.id);
    setMessages((prev) =>
      [...prev, msg].sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
    );
  }, []);

  useEffect(() => {
    if (!conversationId) {
      setMessages([]);
      seenIds.current = new Set();
      return;
    }
    let cancelled = false;
    setLoading(true);
    seenIds.current = new Set();
    getSupportMessages(conversationId)
      .then((data) => {
        if (cancelled) return;
        const list = data || [];
        list.forEach((m) => seenIds.current.add(m.id));
        setMessages(list);
        markSupportRead(conversationId).catch(() => {});
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  useEffect(() => {
    if (!conversationId || !supabase) return;
    const channel = supabase
      .channel(`support-agent:${conversationId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "support_messages",
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          upsertMessage(payload.new);
          markSupportRead(conversationId).catch(() => {});
        }
      )
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [conversationId, upsertMessage]);

  const send = useCallback(
    async ({ body, file }) => {
      if (!conversationId) return;
      setSending(true);
      try {
        const msg = await sendSupportMessage({ conversationId, body, file });
        upsertMessage(msg);
        return msg;
      } finally {
        setSending(false);
      }
    },
    [conversationId, upsertMessage]
  );

  return { messages, loading, sending, send };
};

/**
 * Agent conversation list with realtime refresh.
 * @param {Object} filters - { status, mine }
 */
export const useSupportConversations = (filters) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const filtersKey = JSON.stringify(filters || {});

  const refresh = useCallback(() => {
    setLoading(true);
    return getSupportConversations(filters)
      .then((data) => setConversations(data || []))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Any change to conversations (new msg bumps last_message_at, new conversation,
  // status/assignment change) triggers a lightweight re-fetch.
  useEffect(() => {
    if (!supabase) return;
    const channel = supabase
      .channel("support-conversations")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "support_conversations" },
        () => refresh()
      )
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [refresh]);

  return { conversations, loading, refresh };
};
